import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Tenant } from '@innovation-os-ng/core/models';
import { skipper, TenantSelectors } from '@innovation-os-ng/core/state';
import { KeyEventData } from '@innovation-os-ng/ui/chart-ng';
import { LoaderModule } from '@innovation-os-ng/ui/loader';
import { StackedViewsService } from '@innovation-os-ng/ui/stacked-views';
import { Ui2CardSlideshowModule } from '@innovation-os-ng/ui/ui2-card-slideshow';
import { CompoundFilter, LeafFilter, RangeUtil } from '@itonics/filters';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import { Store } from '@ngrx/store';
import { firstValueFrom, take, takeUntil } from 'rxjs';
import { StackTemplate } from '../../../feed-widget-v2/constants/defaults-from-explorer';
import { BaseEntity } from '../../../shared/model/entity';
import { ClusterModel, ClusterPostDataModel, DateFilter } from '../../model/model';
import { StrongSignalsService } from '../../services/strong-signals.service';
import { ClusterCardComponent } from '../cluster-card/cluster-card.component';
@Component({
  selector: 'signals-cluster-scrollable-list',
  templateUrl: './cluster-scrollable-list.component.html',
  styleUrls: ['./cluster-scrollable-list.component.scss'],
  standalone: true,
  imports: [CommonModule, ClusterCardComponent, LoaderModule, TranslateModule, Ui2CardSlideshowModule, ClusterCardComponent]
})
export class ClusterScrollableListComponent implements OnDestroy, OnChanges {
  /**
   * Receives the title of the scroll list.
   */
  @Input()
  public title: string;

  /**
   * Receives the entity information.
   */
  @Input()
  public entity: BaseEntity;

  /**
   * Filter used to filter global feed.
   *
   * Note: Used so that we can filter cluster list
   * on the basic of date filter if applied by the user.
   */
  @Input()
  public filter: CompoundFilter;

  /**
   * Receives the selected key event data.
   */
  @Input()
  public selectedKeyEvent: KeyEventData | null;

  /**
   * Notifies parent about cluster card clicked.
   */
  @Output()
  public onClusterClick: EventEmitter<ClusterModel> = new EventEmitter();

  /**
   * Cluster list.
   */
  public clusterList: Array<ClusterModel> = [];

  /**
   * Stores the stack cluster id currently opened.
   *
   * Used to make card un-active after stack close.
   */
  public currentlyOpenedStack: Array<string> = [];

  /**
   * Selected key event's first cluster id.
   *
   * Used to only scroll the first item in cluster into the view.
   */
  public firstClusterCardId: string = '';

  /**
   * Loading flag for signals cluster
   */
  public isListLoading: boolean = false;

  /**
   * Custer card's width.
   */
  public readonly clusterCardWidth: number = 240;

  /**
   * Store tenant information
   */
  private tenant: Tenant;

  /**
   * On Component destroy emit the event and notify listeners
   */
  private readonly onDestroy: EventEmitter<void> = new EventEmitter();

  /**
   * Date filter applied to fetch cluster list.
   */
  private clusterDateFilter!: LeafFilter;

  /**
   * Store previously applied date filter
   * so that we can compare from previous
   * and only fetch new data is filter is different
   * from previous.
   */
  private previouslyAppliedFilter!: LeafFilter;

  constructor(
    private readonly strongSignalsService: StrongSignalsService,
    private readonly stackViewService: StackedViewsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly store: Store
  ) {
    this.listenQueryParamsChange();
  }

  /**
   * Listen to input change only.
   *
   * @param changes - Simple change object.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    /**
     * If selected key event is changed then get the
     * first cluster from list related to the selected
     * key event.
     */
    if (changes?.selectedKeyEvent) {
      this.getFirstClusterForSelectedKeyEvent();
    }

    /**
     * If filter change then fetch cluster list again.
     */
    if (changes?.filter) {
      this.refetchClusterList();
    }
  }

  /**
   * On Destroy clear memory
   */
  public ngOnDestroy(): void {
    this.emitOnDestroy();
    this.onDestroy.complete();
  }

  /**
   * Fetch the cluster list.
   */
  private async fetchClusterList(dateFilter?: DateFilter): Promise<void> {
    // Show loader
    this.isListLoading = true;

    // If tenant does not exists then fetch.
    if (!this.tenant) {
      // Fetch the current tenant info.
      await this.fetchCurrentTenant();
    }

    // Prepare post data
    const postData: ClusterPostDataModel = {
      elementUri: this.entity?.uri,
      spaceUri: this.entity?.spaceUri,
      tenantUri: this.tenant?.tenantUri,
      ...(dateFilter && { dateFilter })
    };

    // Get the list
    const clusterList: Array<ClusterModel> = await this.strongSignalsService.fetchAllCluster(postData);

    /**
     * Sort the cluster list according to the key event id.
     *
     * We do this because when we click the key event's the
     * cluster cards should be displayed and be selected in order which
     * looks gives better UX feel.
     */
    this.clusterList = clusterList.sort((a, b) => a.keyEventId - b.keyEventId);

    // Destroy active listeners if pre exits.
    this.emitOnDestroy();

    /**
     * If we have cluster's then only listen to stack
     * events. Else there is no use.
     */
    if (this.clusterList) {
      this.listenStackViewOpen();
      this.listenStackViewClose();
    }

    // hide the loader
    this.isListLoading = false;
  }

  /**
   * Notifies parent on cluster click
   *
   * @param cluster - Selected cluster
   */
  public notifyClusterClick(cluster: ClusterModel): void {
    this.onClusterClick.emit(cluster);
  }

  /**
   * Fetch the current tenant information.
   */
  private async fetchCurrentTenant(): Promise<void> {
    this.tenant = await firstValueFrom(this.store.select(TenantSelectors.selectCurrentTenant).pipe(skipper()));
    console.assert(!!this.tenant, 'tenant should not be null');
  }

  /**
   * Listen to stack view open
   */
  private listenStackViewOpen(): void {
    this.stackViewService.onOpenView.pipe(takeUntil(this.onDestroy)).subscribe((headerInfo) => {
      if (!this.currentlyOpenedStack.includes(headerInfo.uri)) {
        this.currentlyOpenedStack.push(headerInfo.uri);
      }
    });
  }

  /**
   * Listen to stack view open
   */
  private listenStackViewClose(): void {
    this.stackViewService.onCloseView.pipe(takeUntil(this.onDestroy)).subscribe((headerInfo) => {
      if (this.currentlyOpenedStack.includes(headerInfo.uri)) {
        this.currentlyOpenedStack = this.currentlyOpenedStack.filter((uri) => uri !== headerInfo.uri);
      }
    });
  }

  /**
   * Listen query params on initial load
   * so that we can store stack info if stack
   * is present is url and highlight the respective
   * cluster card.
   */
  private listenQueryParamsChange(): void {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      /**
       * If only we have stack in url
       */
      if (params?.stack) {
        const clusterIdArray: Array<string> = params?.stack?.replace(',', '')?.split(`${StackTemplate.CLUSTER}:`);
        clusterIdArray?.shift();

        /**
         * If only we have clusterIds
         */
        if (clusterIdArray?.length) {
          this.currentlyOpenedStack.push(...clusterIdArray);
        }
      }
    });
  }

  /**
   * Get first item in the cluster list.
   */
  private getFirstClusterForSelectedKeyEvent(): void {
    const selectedKeyEventClusterList: Array<ClusterModel> = this.clusterList?.filter((c) => c.keyEventId === this.selectedKeyEvent?.uri);

    // Store first cluster for the selected key event.
    this.firstClusterCardId = selectedKeyEventClusterList?.[0]?.clusterId || '';
  }

  /**
   * Fetch the cluster list on the basic of applied filter.
   */
  private refetchClusterList(): void {
    // Get the date filter.
    this.clusterDateFilter = this.strongSignalsService.getDateFilter(this.filter);

    /**
     * If no date exits then exit.
     */
    if (!this.clusterDateFilter) {
      return;
    }

    /**
     * If previously applied date filter exits
     * and if same then we don't want to fetch again.
     */
    if (this.previouslyAppliedFilter && this.previouslyAppliedFilter?.equals(this.clusterDateFilter)) {
      return;
    }

    // Prepare the date filter.
    const dateFilterToFetchClusterList: DateFilter = {
      start: RangeUtil.convert(this.clusterDateFilter?.getRange().gt ?? null),
      end: RangeUtil.convert(this.clusterDateFilter?.getRange().lt ?? null)
    };

    // Fetch with date filter applied.
    this.fetchClusterList(dateFilterToFetchClusterList);

    // Store the date filter.
    this.previouslyAppliedFilter = this.clusterDateFilter;
  }

  /**
   * Notifies to listener to
   * close the subs.
   */
  private emitOnDestroy(): void {
    this.onDestroy.next();
  }
}
